import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import { useSearchParam } from "react-use";
import SpringContainer from "../../components/containers/SpringContainer";
import { ROUTE_DESTINATIONS } from "../../constants/routes";
import {
  DestinationStatusEnum,
  DestinationTypeEnum,
  DestinationTypeMap,
} from "../../enums/destination";
import { Destination as DestinationModel } from "../../models/destination";
import { useAppSelector } from "../../redux/hooks";
import {
  useCreateDestinationMutation,
  useGetDestinationByIdQuery,
  useTestDestinationMutation,
  useUpdateDestinationByIdMutation,
} from "../../redux/services/destinations";
import { RootState } from "../../redux/store";
import { ensureTrailingSlash } from "../../utils";
import Shopify from "./Shopify";
import Wordpress from "./WordPress";
import DestinationDeleteModal from "./components/modals/DestinationDeleteModal";

export const mask = "•";
export const secretKeyMask = mask.repeat(10);

const Destination = () => {
  const navigate = useNavigate();

  const destinationId = useParams().id; // Edit
  const destinationType = useSearchParam("type");

  const selectedAccount = useAppSelector(
    (state: RootState) => state.user.selectedAccount
  );

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [
    createDestination,
    {
      isLoading: isCreateLoading,
      isSuccess: isCreateSuccess,
      isError: isCreateError,
      error: createErrorMessage,
    },
  ] = useCreateDestinationMutation();

  const [
    updateDestination,
    {
      isLoading: isUpdateLoading,
      isSuccess: isUpdateSuccess,
      isError: isUpdateError,
      error: updateErrorMessage,
    },
  ] = useUpdateDestinationByIdMutation();

  const [
    testDestination,
    {
      isLoading: isTestLoading,
      isError: isTestError,
      data: testSuccessData,
      error: testErrorMessage,
    },
  ] = useTestDestinationMutation();

  const { data: destination, refetch } = useGetDestinationByIdQuery(
    {
      account_id: selectedAccount?.account_id,
      destination_id: destinationId,
    },
    { skip: !destinationId }
  );

  useEffect(() => {
    if (destinationId) {
      refetch();
    }
  }, [destinationId, refetch]);

  useEffect(() => {
    if (isCreateSuccess || isUpdateSuccess) {
      navigate(ROUTE_DESTINATIONS);
    }
  }, [isCreateSuccess, isUpdateSuccess, navigate]);

  useEffect(() => {
    const isError =
      isCreateError ||
      isUpdateError ||
      createErrorMessage ||
      updateErrorMessage;

    if (isError) {
      toast.error(
        ((createErrorMessage || updateErrorMessage) as any)?.data?.detail ??
          "Something went wrong."
      );
    }
  }, [isCreateError, isUpdateError, createErrorMessage, updateErrorMessage]);

  const handleSubmit = async (values: any, test?: boolean) => {
    if (!selectedAccount?.account_id) return;

    const isWordPress =
      values.destination_type_id === DestinationTypeEnum.WORDPRESS;
    const isShopify =
      values.destination_type_id === DestinationTypeEnum.SHOPIFY;

    const payload: any = {
      ...values,
      account_id: selectedAccount.account_id,
      destination: {
        ...values.destination,
      },
    };

    if (isWordPress) {
      payload.url = ensureTrailingSlash(values.destination.url);
      payload.destination.post_delay_hours =
        values.destination.status === DestinationStatusEnum.FUTURE
          ? values.destination.post_delay_hours
          : null;
      payload.destination.random_post_hours =
        values.destination.status === DestinationStatusEnum.FUTURE
          ? values.destination.random_post_hours
          : null;
    }

    if (isShopify) {
      payload.destination.store_name = values.destination.store_name;
    }

    if (test) {
      try {
        await testDestination(payload).unwrap();
        setShowSaveModal(true);
      } catch (error) {
        console.error("Test failed:", error);
      }
      return;
    }

    // Save logic
    try {
      if (destinationId) {
        await updateDestination({
          ...payload,
          destination_id: destinationId,
          template_id_list: values.template_id_list ?? [],
        }).unwrap();
      } else {
        await createDestination(payload).unwrap();
      }
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  if (!destinationType) return <Navigate to={ROUTE_DESTINATIONS} />;

  const isWordPress =
    destinationType === DestinationTypeMap[DestinationTypeEnum.WORDPRESS];

  const isShopify =
    destinationType === DestinationTypeMap[DestinationTypeEnum.SHOPIFY];

  return (
    <SpringContainer>
      {isWordPress && (
        <Wordpress
          destination={destination as DestinationModel}
          isTestLoading={isTestLoading}
          isTestError={isTestError}
          testErrorMessage={testErrorMessage}
          showSaveModal={showSaveModal}
          testSuccessMessage={testSuccessData?.message}
          isSaving={isCreateLoading || isUpdateLoading}
          onSaveSuccess={() => setShowSaveModal(false)}
          handleTest={(values) => handleSubmit(values, true)}
          handleSubmit={(values) => handleSubmit(values)}
          handleDelete={() => setShowDeleteModal(true)}
        />
      )}

      {isShopify && (
        <Shopify
          destination={destination as DestinationModel}
          isTestLoading={isTestLoading}
          isTestError={isTestError}
          testErrorMessage={testErrorMessage}
          showSaveModal={showSaveModal}
          testSuccessMessage={testSuccessData?.message}
          isSaving={isCreateLoading || isUpdateLoading}
          onSaveSuccess={() => setShowSaveModal(false)}
          handleTest={(values) => handleSubmit(values, true)}
          handleSubmit={(values) => handleSubmit(values)}
          handleDelete={() => setShowDeleteModal(true)}
        />
      )}

      {showDeleteModal && (
        <DestinationDeleteModal
          destinationId={destinationId}
          onRequestClose={() => setShowDeleteModal(false)}
        />
      )}
    </SpringContainer>
  );
};

export default Destination;
